import { Task } from "@/types/v2";

export type DependencyTagKind = "depends_on" | "precursor_for";

export interface TaskDependencyGroupColor {
  border: string;
  text: string;
  badgeBackground: string;
  cardBackground: string;
}

export interface TaskDependencyTag {
  id: string;
  kind: DependencyTagKind;
  text: string;
  unresolved: boolean;
}

export interface TaskDependencyContext {
  tags: TaskDependencyTag[];
  groupColor: TaskDependencyGroupColor | null;
  hasUnresolvedDependsOn: boolean;
}

interface TaskReference {
  key: string;
  title: string;
  status: string | null;
}

interface InternalTaskNode {
  task: Task;
  label: string;
  tokens: Set<string>;
}

const FINISHED_STATUSES = new Set(["finished", "completed", "done"]);
const TASK_CODE_REGEX = /\b[A-Z]{2,}[A-Z0-9]*(?:-[A-Z0-9]+)+\b/g;

const OBJECTIVE_PREFIX_MAP: Record<string, string[]> = {
  vaccination: ["VAC"],
  challenge: ["CHA", "CHL"],
  pre_challenge: ["DIL", "PRE"],
  mortality: ["MOR"],
  environmental: ["ENV"],
  environmental_modifier: ["EVM", "ENV"],
  fish_transfer: ["TRF", "FTR"],
  fish_import: ["IMP", "FIM"],
  fish_reconciliation: ["REC"],
};

const DEFAULT_CONTEXT: TaskDependencyContext = {
  tags: [],
  groupColor: null,
  hasUnresolvedDependsOn: false,
};

const normalizeToken = (value: string): string => value.trim().toLowerCase();

const getObjectiveId = (task: Task): string => {
  const objective = task.objective as unknown;

  if (typeof objective === "string") {
    return objective;
  }

  if (objective && typeof objective === "object" && "id" in objective) {
    const objectiveId = (objective as { id?: unknown }).id;
    return typeof objectiveId === "string" ? objectiveId : "";
  }

  return "";
};

const getStringValue = (
  record: Record<string, unknown>,
  key: string,
): string | null => {
  const value = record[key];
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  return normalized ? normalized : null;
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const getFirstStringValue = (
  record: Record<string, unknown>,
  keys: string[],
): string | null => {
  for (const key of keys) {
    const value = getStringValue(record, key);
    if (value) {
      return value;
    }
  }

  return null;
};

const normalizeReference = (value: unknown): TaskReference | null => {
  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    return { key: normalized, title: normalized, status: null };
  }

  if (!isObjectRecord(value)) {
    return null;
  }

  const nestedReferenceKeys = [
    "task",
    "relatedTask",
    "linkedTask",
    "dependencyTask",
    "dependsOnTask",
    "parentTask",
    "childTask",
    "challengeTask",
    "preChallengeTask",
    "mainTask",
  ];

  for (const nestedKey of nestedReferenceKeys) {
    if (nestedKey in value) {
      const nestedReference = normalizeReference(value[nestedKey]);
      if (nestedReference) {
        return nestedReference;
      }
    }
  }

  const key = getFirstStringValue(value, [
    "key",
    "id",
    "taskKey",
    "relatedTaskKey",
    "linkedTaskKey",
    "dependencyTaskKey",
    "challengeTaskKey",
    "preChallengeTaskKey",
    "parentTaskKey",
    "childTaskKey",
  ]);
  const title =
    getFirstStringValue(value, [
      "additionalTitle",
      "title",
      "name",
      "label",
      "taskTitle",
      "code",
      "reference",
    ]) ?? key;
  const status = getFirstStringValue(value, ["status", "taskStatus", "state"]);

  if (!key && !title) {
    return null;
  }

  return {
    key: key ?? title ?? "unknown-reference",
    title: title ?? key ?? "Unknown task",
    status,
  };
};

const normalizeReferenceList = (value: unknown): TaskReference[] => {
  if (Array.isArray(value)) {
    return value
      .map(normalizeReference)
      .filter((item): item is TaskReference => item !== null);
  }

  const single = normalizeReference(value);
  return single ? [single] : [];
};

const createReferenceFromText = (text: string): TaskReference => {
  const normalized = text.trim().replace(/[.,;:!?]+$/g, "");
  return {
    key: normalized,
    title: normalized,
    status: null,
  };
};

const extractCodesFromText = (text: string): TaskReference[] => {
  const matches = text.match(TASK_CODE_REGEX);
  if (!matches || matches.length === 0) {
    const raw = text
      .split(/,|\band\b/gi)
      .map((part) => part.trim())
      .filter(Boolean);

    if (raw.length === 0) {
      return [];
    }

    return raw.map((part) => createReferenceFromText(part));
  }

  return Array.from(new Set(matches)).map((code) =>
    createReferenceFromText(code),
  );
};

const extractReferencesFromText = (
  value: unknown,
  kind: "depends_on" | "blocks",
): TaskReference[] => {
  if (typeof value !== "string") {
    return [];
  }

  const text = value.trim();
  if (!text) {
    return [];
  }

  const patterns: RegExp[] =
    kind === "depends_on"
      ? [
          /\b(?:linked to|linked with|depends on|dependent on|blocked by|requires)\s+([^.!\n]+)/gi,
        ]
      : [/\b(?:blocks|blocking|precursor for|prerequisite for)\s+([^.!\n]+)/gi];

  const references: TaskReference[] = [];

  patterns.forEach((pattern) => {
    let match: RegExpExecArray | null = pattern.exec(text);
    while (match) {
      const segment = match[1]?.trim();
      if (segment) {
        references.push(...extractCodesFromText(segment));
      }

      match = pattern.exec(text);
    }
  });

  return references;
};

const getTaskPrefixes = (task: Task): string[] => {
  const objectiveId = getObjectiveId(task);
  const mapped = OBJECTIVE_PREFIX_MAP[objectiveId];
  if (mapped && mapped.length > 0) {
    return mapped;
  }

  if (!objectiveId) {
    return [];
  }

  return [objectiveId.replace(/_/g, "").slice(0, 3).toUpperCase()];
};

const getTaskLabel = (task: Task): string => {
  const additionalTitle = task.additionalTitle?.trim();
  if (additionalTitle) {
    return additionalTitle;
  }

  const title = task.title?.trim();
  if (title) {
    return title;
  }

  const codeInDescription = task.description.match(TASK_CODE_REGEX)?.[0];
  if (codeInDescription) {
    return codeInDescription;
  }

  return task.key;
};

const getTaskDisplayTitle = (task: Task): string | null => {
  const additionalTitle = task.additionalTitle?.trim();
  if (additionalTitle) {
    return additionalTitle;
  }

  const title = task.title?.trim();
  return title || null;
};

const normalizeGroupingValue = (value: string): string => {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}]+/gu, "");
};

const getTaskTokens = (task: Task): Set<string> => {
  const tokens = new Set<string>();

  const addToken = (value: unknown) => {
    if (typeof value !== "string") {
      return;
    }

    const normalized = normalizeToken(value);
    if (!normalized) {
      return;
    }

    tokens.add(normalized);
  };

  addToken(task.key);
  addToken(task.title);
  addToken(task.additionalTitle);
  addToken(task.studyTitle);
  addToken(task.description);

  [
    task.key,
    task.title,
    task.additionalTitle,
    task.studyTitle,
    task.description,
  ]
    .filter((value): value is string => typeof value === "string")
    .forEach((value) => {
      value.match(TASK_CODE_REGEX)?.forEach((code) => addToken(code));
    });

  if (task.additionalTitle) {
    getTaskPrefixes(task).forEach((prefix) => {
      addToken(`${prefix}-${task.additionalTitle}`);
    });
  }

  return tokens;
};

const collectStructuredReferences = (
  source: Record<string, unknown>,
  keys: string[],
): TaskReference[] => {
  const references: TaskReference[] = [];

  keys.forEach((key) => {
    references.push(...normalizeReferenceList(source[key]));
  });

  return references;
};

const collectDetailsReferences = (
  details: Record<string, unknown>,
): { dependsOn: TaskReference[]; blocks: TaskReference[] } => {
  const dependsOn: TaskReference[] = [];
  const blocks: TaskReference[] = [];

  dependsOn.push(...normalizeReferenceList(details.preChallengeTask));
  blocks.push(...normalizeReferenceList(details.dependentChallengeTask));

  Object.entries(details).forEach(([key, value]) => {
    if (
      !/(depend|link|block|prereq|precursor|parent|child|required)/i.test(key)
    ) {
      return;
    }

    if (key === "preChallengeTask" || key === "dependentChallengeTask") {
      return;
    }

    if (/(dependent|child|generated|blocks|precursor|follower)/i.test(key)) {
      blocks.push(...normalizeReferenceList(value));
      return;
    }

    dependsOn.push(...normalizeReferenceList(value));
  });

  return { dependsOn, blocks };
};

const isUnresolvedByStatus = (status: string | null): boolean => {
  if (!status) {
    return true;
  }

  return !FINISHED_STATUSES.has(status.toLowerCase());
};

const hashToColor = (seed: string): TaskDependencyGroupColor => {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(index);
    hash |= 0;
  }

  const hue = Math.abs(hash) % 360;

  return {
    border: `hsl(${hue}, 72%, 45%)`,
    text: `hsl(${hue}, 70%, 28%)`,
    badgeBackground: `hsla(${hue}, 85%, 89%, 0.95)`,
    cardBackground: `hsla(${hue}, 88%, 95%, 0.75)`,
  };
};

const addTag = (
  contextMap: Record<string, TaskDependencyContext>,
  taskKey: string,
  tag: TaskDependencyTag,
) => {
  const context = contextMap[taskKey];
  if (!context) {
    return;
  }

  if (context.tags.some((existing) => existing.id === tag.id)) {
    return;
  }

  context.tags.push(tag);

  if (tag.kind === "depends_on" && tag.unresolved) {
    context.hasUnresolvedDependsOn = true;
  }
};

const resolveReferenceTaskKeys = (
  reference: TaskReference,
  tokenIndex: Map<string, Set<string>>,
  taskByKey: Map<string, InternalTaskNode>,
): string[] => {
  const resolved = new Set<string>();

  const addMatches = (token: string) => {
    const normalized = normalizeToken(token);
    if (!normalized) {
      return;
    }

    if (taskByKey.has(normalized)) {
      resolved.add(normalized);
    }

    const candidates = tokenIndex.get(normalized);
    if (candidates) {
      candidates.forEach((candidate) => resolved.add(candidate));
    }
  };

  addMatches(reference.key);
  addMatches(reference.title);

  [reference.key, reference.title].filter(Boolean).forEach((value) => {
    value.match(TASK_CODE_REGEX)?.forEach((code) => addMatches(code));
  });

  return Array.from(resolved);
};

const connectAsDependency = (
  contextMap: Record<string, TaskDependencyContext>,
  adjacency: Map<string, Set<string>>,
  taskNodes: Map<string, InternalTaskNode>,
  dependentTaskKey: string,
  prerequisiteTaskKey: string,
  unresolved: boolean,
  idPrefix: string,
) => {
  const dependentTask = taskNodes.get(dependentTaskKey);
  const prerequisiteTask = taskNodes.get(prerequisiteTaskKey);

  if (
    !dependentTask ||
    !prerequisiteTask ||
    dependentTaskKey === prerequisiteTaskKey
  ) {
    return;
  }

  if (adjacency.get(dependentTaskKey)?.has(prerequisiteTaskKey)) {
    return;
  }

  addTag(contextMap, dependentTaskKey, {
    id: `${idPrefix}-depends-${dependentTaskKey}-${prerequisiteTaskKey}`,
    kind: "depends_on",
    text: `Related to ${prerequisiteTask.label}`,
    unresolved,
  });

  addTag(contextMap, prerequisiteTaskKey, {
    id: `${idPrefix}-related-${prerequisiteTaskKey}-${dependentTaskKey}`,
    kind: "precursor_for",
    text: `Related to ${dependentTask.label}`,
    unresolved: false,
  });

  adjacency.get(dependentTaskKey)?.add(prerequisiteTaskKey);
  adjacency.get(prerequisiteTaskKey)?.add(dependentTaskKey);
};

const pairByIndex = (
  left: string[],
  right: string[],
): Array<[string, string]> => {
  const pairs: Array<[string, string]> = [];
  if (left.length === 0 || right.length === 0) {
    return pairs;
  }

  const maxLength = Math.max(left.length, right.length);
  for (let index = 0; index < maxLength; index += 1) {
    const leftItem = left[Math.min(index, left.length - 1)];
    const rightItem = right[Math.min(index, right.length - 1)];
    pairs.push([leftItem, rightItem]);
  }

  return pairs;
};

export const buildTaskDependencyContext = (
  tasks: Task[],
): Record<string, TaskDependencyContext> => {
  const contextMap: Record<string, TaskDependencyContext> = {};
  const taskNodes = new Map<string, InternalTaskNode>();
  const tokenIndex = new Map<string, Set<string>>();
  const adjacency = new Map<string, Set<string>>();

  tasks.forEach((task) => {
    contextMap[task.key] = {
      tags: [],
      groupColor: null,
      hasUnresolvedDependsOn: false,
    };

    const node: InternalTaskNode = {
      task,
      label: getTaskLabel(task),
      tokens: getTaskTokens(task),
    };

    taskNodes.set(task.key, node);
    adjacency.set(task.key, new Set<string>());

    node.tokens.forEach((token) => {
      const bucket = tokenIndex.get(token) ?? new Set<string>();
      bucket.add(task.key);
      tokenIndex.set(token, bucket);
    });
  });

  const dependsOnFields = [
    "dependencies",
    "dependsOn",
    "blockedBy",
    "prerequisiteTask",
    "prerequisiteTasks",
    "linkedTask",
    "linkedTasks",
    "mainTask",
    "parentTask",
    "sourceTask",
    "requiredTask",
    "requiredTasks",
  ];

  const blocksFields = [
    "dependentTasks",
    "dependents",
    "blocks",
    "generatedTasks",
    "childTasks",
    "followerTasks",
  ];

  tasks.forEach((task) => {
    const taskRecord = task as Task & Record<string, unknown>;

    const dependsOnReferences: TaskReference[] = [];
    const blocksReferences: TaskReference[] = [];

    dependsOnReferences.push(
      ...collectStructuredReferences(taskRecord, dependsOnFields),
    );
    blocksReferences.push(
      ...collectStructuredReferences(taskRecord, blocksFields),
    );

    dependsOnReferences.push(
      ...extractReferencesFromText(taskRecord.description, "depends_on"),
      ...extractReferencesFromText(taskRecord.title, "depends_on"),
    );

    blocksReferences.push(
      ...extractReferencesFromText(taskRecord.description, "blocks"),
      ...extractReferencesFromText(taskRecord.title, "blocks"),
    );

    if (isObjectRecord(taskRecord.details)) {
      const fromDetails = collectDetailsReferences(taskRecord.details);
      dependsOnReferences.push(...fromDetails.dependsOn);
      blocksReferences.push(...fromDetails.blocks);
    }

    if (!taskNodes.has(task.key)) {
      return;
    }

    dependsOnReferences.forEach((reference) => {
      const targets = resolveReferenceTaskKeys(
        reference,
        tokenIndex,
        taskNodes,
      ).filter((targetKey) => targetKey !== task.key);

      if (targets.length === 0) {
        addTag(contextMap, task.key, {
          id: `depends-external-${normalizeToken(reference.title)}`,
          kind: "depends_on",
          text: `Related to ${reference.title}`,
          unresolved: true,
        });
        return;
      }

      targets.forEach((targetKey) => {
        const targetNode = taskNodes.get(targetKey);
        if (!targetNode) {
          return;
        }

        const unresolved =
          reference.status !== null
            ? isUnresolvedByStatus(reference.status)
            : targetNode.task.status !== "finished";

        connectAsDependency(
          contextMap,
          adjacency,
          taskNodes,
          task.key,
          targetKey,
          unresolved,
          "explicit-depends",
        );
      });
    });

    blocksReferences.forEach((reference) => {
      const targets = resolveReferenceTaskKeys(
        reference,
        tokenIndex,
        taskNodes,
      ).filter((targetKey) => targetKey !== task.key);

      if (targets.length === 0) {
        addTag(contextMap, task.key, {
          id: `precursor-external-${normalizeToken(reference.title)}`,
          kind: "precursor_for",
          text: `Related to ${reference.title}`,
          unresolved: false,
        });
        return;
      }

      targets.forEach((targetKey) => {
        connectAsDependency(
          contextMap,
          adjacency,
          taskNodes,
          targetKey,
          task.key,
          task.status !== "finished",
          "explicit-blocks",
        );
      });
    });
  });

  // Deterministic fallback grouping for known task families that should be linked
  // even when backend reference fields are missing or not resolvable.
  const tasksByCompositeKey = new Map<
    string,
    {
      challenge: string[];
      pre_challenge: string[];
      environmental: string[];
      environmental_modifier: string[];
    }
  >();
  const tasksByStudyKey = new Map<
    string,
    {
      challenge: string[];
      pre_challenge: string[];
      environmental: string[];
      environmental_modifier: string[];
    }
  >();

  tasks.forEach((task) => {
    const displayTitle = getTaskDisplayTitle(task);
    if (!displayTitle) {
      return;
    }

    const studyTitle = task.studyTitle?.trim() ?? "";
    const normalizedTitle = normalizeGroupingValue(displayTitle);
    const normalizedStudy = normalizeGroupingValue(studyTitle);
    const compositeKey = `${normalizedTitle}|${normalizedStudy}`;
    const objectiveId = getObjectiveId(task);

    const bucket = tasksByCompositeKey.get(compositeKey) ?? {
      challenge: [],
      pre_challenge: [],
      environmental: [],
      environmental_modifier: [],
    };

    if (objectiveId === "challenge") {
      bucket.challenge.push(task.key);
    } else if (objectiveId === "pre_challenge") {
      bucket.pre_challenge.push(task.key);
    } else if (objectiveId === "environmental") {
      bucket.environmental.push(task.key);
    } else if (objectiveId === "environmental_modifier") {
      bucket.environmental_modifier.push(task.key);
    }

    tasksByCompositeKey.set(compositeKey, bucket);

    if (normalizedStudy) {
      const studyBucket = tasksByStudyKey.get(normalizedStudy) ?? {
        challenge: [],
        pre_challenge: [],
        environmental: [],
        environmental_modifier: [],
      };

      if (objectiveId === "challenge") {
        studyBucket.challenge.push(task.key);
      } else if (objectiveId === "pre_challenge") {
        studyBucket.pre_challenge.push(task.key);
      } else if (objectiveId === "environmental") {
        studyBucket.environmental.push(task.key);
      } else if (objectiveId === "environmental_modifier") {
        studyBucket.environmental_modifier.push(task.key);
      }

      tasksByStudyKey.set(normalizedStudy, studyBucket);
    }
  });

  tasksByCompositeKey.forEach((bucket, compositeKey) => {
    const challengePairs = pairByIndex(bucket.challenge, bucket.pre_challenge);
    challengePairs.forEach(([challengeTaskKey, preChallengeTaskKey]) => {
      const preChallengeTask = taskNodes.get(preChallengeTaskKey)?.task;
      connectAsDependency(
        contextMap,
        adjacency,
        taskNodes,
        challengeTaskKey,
        preChallengeTaskKey,
        preChallengeTask ? preChallengeTask.status !== "finished" : true,
        `fallback-challenge-${compositeKey}`,
      );
    });

    const environmentalPairs = pairByIndex(
      bucket.environmental_modifier,
      bucket.environmental,
    );
    environmentalPairs.forEach(
      ([environmentalModifierTaskKey, environmentalTaskKey]) => {
        const environmentalTask = taskNodes.get(environmentalTaskKey)?.task;
        connectAsDependency(
          contextMap,
          adjacency,
          taskNodes,
          environmentalModifierTaskKey,
          environmentalTaskKey,
          environmentalTask ? environmentalTask.status !== "finished" : true,
          `fallback-environment-${compositeKey}`,
        );
      },
    );
  });

  // Secondary fallback: if title-based pairing did not connect tasks,
  // pair by study only (common in API payloads where challenge/dilution
  // title formats differ but study is shared).
  tasksByStudyKey.forEach((bucket, studyKey) => {
    const challengePairs = pairByIndex(bucket.challenge, bucket.pre_challenge);
    challengePairs.forEach(([challengeTaskKey, preChallengeTaskKey]) => {
      const preChallengeTask = taskNodes.get(preChallengeTaskKey)?.task;
      connectAsDependency(
        contextMap,
        adjacency,
        taskNodes,
        challengeTaskKey,
        preChallengeTaskKey,
        preChallengeTask ? preChallengeTask.status !== "finished" : true,
        `fallback-study-challenge-${studyKey}`,
      );
    });

    const environmentalPairs = pairByIndex(
      bucket.environmental_modifier,
      bucket.environmental,
    );
    environmentalPairs.forEach(
      ([environmentalModifierTaskKey, environmentalTaskKey]) => {
        const environmentalTask = taskNodes.get(environmentalTaskKey)?.task;
        connectAsDependency(
          contextMap,
          adjacency,
          taskNodes,
          environmentalModifierTaskKey,
          environmentalTaskKey,
          environmentalTask ? environmentalTask.status !== "finished" : true,
          `fallback-study-environment-${studyKey}`,
        );
      },
    );
  });

  const visited = new Set<string>();

  tasks.forEach((task) => {
    if (visited.has(task.key)) {
      return;
    }

    const neighbors = adjacency.get(task.key);
    if (!neighbors || neighbors.size === 0) {
      visited.add(task.key);
      return;
    }

    const queue: string[] = [task.key];
    const component: string[] = [];

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current || visited.has(current)) {
        continue;
      }

      visited.add(current);
      component.push(current);

      const currentNeighbors = adjacency.get(current);
      currentNeighbors?.forEach((neighbor) => {
        if (!visited.has(neighbor)) {
          queue.push(neighbor);
        }
      });
    }

    if (component.length === 0) {
      return;
    }

    const color = hashToColor(component.slice().sort().join("|"));
    component.forEach((taskKey) => {
      const context = contextMap[taskKey];
      if (context) {
        context.groupColor = color;
      }
    });
  });

  tasks.forEach((task) => {
    const context = contextMap[task.key];
    if (!context) {
      contextMap[task.key] = { ...DEFAULT_CONTEXT };
      return;
    }

    context.tags.sort((first, second) => {
      if (first.kind === second.kind) {
        return first.text.localeCompare(second.text);
      }

      return first.kind === "depends_on" ? -1 : 1;
    });
  });

  return contextMap;
};
